import {useCallback, useEffect, useRef} from 'react';
import {scenarioCasesApi} from '@api';
import type {
    ScenarioCaseExecutionUpdatedLiveNotification,
    ScenarioCaseExecutionUpdatedPayload,
} from '@api';
import {useDispatch} from './common';
import {useMercure} from './useMercure';

type UseScenarioCaseExecutionUpdatesOptions = {
    enabled?: boolean;
    onUpdate?: (payload: ScenarioCaseExecutionUpdatedPayload) => void;
};

export const useScenarioCaseExecutionUpdates = ({
    enabled = true,
    onUpdate,
}: UseScenarioCaseExecutionUpdatesOptions = {}) => {
    const dispatch = useDispatch();
    const refreshTimeoutRef = useRef<number | null>(null);

    const scheduleRefresh = useCallback(() => {
        if (refreshTimeoutRef.current !== null) {
            return;
        }

        refreshTimeoutRef.current = window.setTimeout(() => {
            refreshTimeoutRef.current = null;
            dispatch(
                scenarioCasesApi.util.invalidateTags([
                    'ScenarioCase',
                    'ScenarioCaseExecution',
                    'ScenarioCaseSummary',
                ])
            );
        }, 250);
    }, [dispatch]);

    const handleMessage = useCallback(
        (data: unknown) => {
            if (
                !data ||
                typeof data !== 'object' ||
                !('type' in data) ||
                data.type !== 'scenario_case_execution_updated'
            ) {
                return;
            }

            const notification =
                data as ScenarioCaseExecutionUpdatedLiveNotification;

            scheduleRefresh();
            onUpdate?.(notification.payload);
        },
        [onUpdate, scheduleRefresh]
    );

    useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current !== null) {
                window.clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    return useMercure({
        topic: '/scenario-cases/executions',
        enabled,
        onMessage: handleMessage,
    });
};
